import React from 'react';
import { Plus, Trash2, Image, GripVertical } from 'lucide-react';
import ColorEditor from '../ColorEditor';
import { resolveImageUrl } from '../../utils/imageUtils';

const ServicesEditor = ({ content, onContentChange, onArrayFieldChange, onAddArrayItem, onRemoveArrayItem, uploading, onImageUpload, onOpenImagePicker, businessInfo, site, onCreateCustomTheme }) => {
  // Initialize services if empty
  React.useEffect(() => {
    if (!content.services || content.services.length === 0) {
      onContentChange('services', [
        { title: 'Premium Service', description: 'High-quality service tailored to your specific needs with attention to detail', imageUrl: '' },
        { title: 'Expert Consultation', description: 'Professional guidance and strategic advice from our experienced team', imageUrl: '' },
        { title: 'Ongoing Support', description: 'Continuous assistance and maintenance to ensure optimal results', imageUrl: '' }
      ]);
    }
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Title
        </label>
        <input
          type="text"
          value={content.title || ''}
          onChange={(e) => onContentChange('title', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Our Services"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Section Description
        </label>
        <textarea
          value={content.description || ''}
          onChange={(e) => onContentChange('description', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Discover what we can do for you with our comprehensive range of services."
          rows="2"
        />
      </div>

      {/* Services Elements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Services elements
        </label>
        <div className="space-y-2">
          {/* Section Heading Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Section heading</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="servicesHeadingToggle"
                type="checkbox" 
                checked={content.showHeading !== false}
                onChange={(e) => onContentChange('showHeading', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="servicesHeadingToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Description Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Description</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="servicesDescriptionToggle"
                type="checkbox" 
                checked={content.showDescription !== false}
                onChange={(e) => onContentChange('showDescription', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="servicesDescriptionToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
          
          {/* Images Toggle */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-700">Images</span>
            <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
              <input 
                id="servicesImagesToggle"
                type="checkbox" 
                checked={content.showImages !== false}
                onChange={(e) => onContentChange('showImages', e.target.checked)}
                className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
              />
              <label htmlFor="servicesImagesToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
            </div>
          </div>
        </div>
      </div>

      {/* Services List */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Services</label>
        <div className="space-y-3">
          {(content.services || []).map((service, index) => (
            <div key={index} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <GripVertical className="w-4 h-4 text-gray-400 mr-2" />
                  <span className="text-sm font-medium text-gray-700">Service {index + 1}</span>
                </div>
                <button
                  onClick={() => onRemoveArrayItem('services', index)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              {/* Service Image */}
              {content.showImages !== false && (
                <div className="mb-3">
                  <div className="flex gap-2">
                    <div 
                      className="flex-1 h-24 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                      onClick={() => document.getElementById(`serviceImageInput${index}`).click()}
                    >
                      {service.imageUrl ? (
                        <img 
                          src={resolveImageUrl(service.imageUrl)} 
                          alt={service.title || `Service ${index + 1}`}
                          className="w-full h-full object-cover rounded"
                        />
                      ) : (
                        <div className="text-center">
                          <Image className="w-6 h-6 text-gray-400 mx-auto mb-1" />
                          <div className="text-xs text-gray-500">
                            {uploading ? 'Uploading...' : 'Click to upload'}
                          </div>
                        </div>
                      )}
                      <input
                        id={`serviceImageInput${index}`}
                        type="file"
                        accept="image/*"
                        onChange={(e) => onImageUpload(e.target.files[0], `services.${index}.imageUrl`)}
                        className="hidden"
                        disabled={uploading}
                      />
                    </div>
                    <button
                      onClick={() => onOpenImagePicker(`services.${index}.imageUrl`)}
                      className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                      disabled={uploading}
                    >
                      <Image className="w-3 h-3 mb-1" />
                      <span>Unsplash</span>
                    </button>
                  </div>
                  
                  {/* Photo Credit for Service Image */}
                  {service.imageUrlCredit && (
                    <div className="text-xs text-gray-500 mt-1">
                      Photo by{' '}
                      <a 
                        href={service.imageUrlCredit.photographerUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {service.imageUrlCredit.photographer}
                      </a>
                      {' '}on Unsplash
                    </div>
                  )}
                </div>
              )}
              
              {/* Service Title */}
              {content.showHeading !== false && (
                <input
                  type="text"
                  value={service.title || ''}
                  onChange={(e) => onArrayFieldChange('services', index, { ...service, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                  placeholder="Service title"
                />
              )}
              
              {/* Service Description */}
              {content.showDescription !== false && (
                <textarea
                  rows={2}
                  value={service.description || ''}
                  onChange={(e) => onArrayFieldChange('services', index, { ...service, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Service description"
                />
              )}
            </div>
          ))}
          <button
            onClick={() => onAddArrayItem('services', { title: '', description: '', imageUrl: '' })}
            className="flex items-center text-sm text-blue-600 hover:text-blue-800"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add service
          </button>
          <div className="text-xs text-gray-500 mt-1">
            You can add {21 - ((content.services?.length || 0) === 0 ? 3 : content.services.length)} more services
          </div>
        </div>
      </div>

      {/* Images Section */}
      {content.showImages !== false && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">Images</label>
          
          {/* Frame Options */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">Frame</label>
            <div className="flex gap-2">
              {[
                { name: 'landscape', icon: '▭' },
                { name: 'square', icon: '⬜' },
                { name: 'portrait', icon: '▯' },
                { name: 'circle', icon: '●' }
              ].map(frame => (
                <button
                  key={frame.name}
                  onClick={() => onContentChange('imageFrame', frame.name)}
                  className={`w-8 h-8 flex items-center justify-center text-lg border rounded ${
                    (content.imageFrame || 'landscape') === frame.name 
                      ? 'bg-gray-200 border-gray-400' 
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  title={frame.name}
                >
                  {frame.icon}
                </button>
              ))}
            </div>
          </div>

          {/* Border Options */}
          <div className="mb-3">
            <label className="block text-xs font-medium text-gray-700 mb-2">Borders</label>
            <select
              value={content.imageBorder || 'none'}
              onChange={(e) => onContentChange('imageBorder', e.target.value)}
              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="none">None</option>
              <option value="all">All</option>
              <option value="bottom">Bottom</option>
            </select>
          </div>
        </div>
      )}

      {/* Text Alignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Text</label>
        <div className="flex gap-2">
          {[
            { name: 'left', icon: '☰' },
            { name: 'center', icon: '☱' },
            { name: 'right', icon: '☲' }
          ].map(align => (
            <button
              key={align.name}
              onClick={() => onContentChange('textAlign', align.name)}
              className={`w-8 h-8 flex items-center justify-center text-lg border rounded ${
                (content.textAlign || 'left') === align.name 
                  ? 'bg-gray-200 border-gray-400' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              title={`Align ${align.name}`}
            >
              {align.icon}
            </button>
          ))}
        </div>
      </div>

      {/* Layout Options */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Layout</label>
        <div className="flex gap-1">
          {[
            { name: 'auto', icon: '⚡', desc: 'Smart layout based on number of services' },
            { name: 'single', icon: '1', desc: 'One column' },
            { name: 'two-column', icon: '2', desc: 'Two columns' },
            { name: 'three-column', icon: '3', desc: 'Three columns' },
            { name: 'four-column', icon: '4', desc: 'Four columns' }
          ].map(layout => (
            <button
              key={layout.name}
              onClick={() => onContentChange('layout', layout.name)}
              className={`flex-1 h-8 flex items-center justify-center text-xs border rounded ${
                (content.layout || 'auto') === layout.name 
                  ? 'bg-blue-100 border-blue-400 text-blue-700' 
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
              title={layout.desc}
            >
              <span className="mr-1">{layout.icon}</span>
              <span className="text-xs">{layout.name === 'auto' ? 'Auto' : layout.name.includes('-') ? layout.name.split('-')[0] : layout.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Card Background Opacity */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Card Background Opacity</label>
        <div className="flex items-center space-x-3">
          <span className="text-xs text-gray-500">Transparent</span>
          <input
            type="range"
            min="0"
            max="100"
            value={content.cardBackgroundOpacity || 100}
            onChange={(e) => onContentChange('cardBackgroundOpacity', parseInt(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <span className="text-xs text-gray-500">Solid</span>
        </div>
        <div className="text-center mt-1">
          <span className="text-xs text-gray-600">{content.cardBackgroundOpacity || 100}%</span>
        </div>
      </div>

      {/* Background Image */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="block text-sm font-medium text-gray-700">Background image</label>
          <div className="relative inline-block w-6 align-middle select-none transition duration-200 ease-in">
            <input 
              id="servicesBackgroundToggle"
              type="checkbox" 
              checked={content.backgroundImage || false}
              onChange={(e) => onContentChange('backgroundImage', e.target.checked)}
              className="toggle-checkbox absolute block w-3 h-3 rounded-full bg-white border-2 appearance-none cursor-pointer"
            />
            <label htmlFor="servicesBackgroundToggle" className="toggle-label block overflow-hidden h-3 rounded-full bg-gray-300 cursor-pointer"></label>
          </div>
        </div>
        {content.backgroundImage && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <div 
                className="flex-1 h-20 bg-gray-100 border-2 border-dashed border-gray-300 rounded flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors relative overflow-hidden"
                onClick={() => document.getElementById('servicesBackgroundImageInput').click()}
              >
                {content.backgroundImageUrl ? (
                  <img 
                    src={content.backgroundImageUrl} 
                    alt="Background" 
                    className="w-full h-full object-cover rounded"
                  />
                ) : (
                  <div className="text-center">
                    <Image className="w-4 h-4 text-gray-400 mx-auto mb-1" />
                    <div className="text-xs text-gray-500">
                      {uploading ? 'Uploading...' : 'Upload'}
                    </div>
                  </div>
                )}
                <input
                  id="servicesBackgroundImageInput"
                  type="file"
                  accept="image/*"
                  onChange={(e) => onImageUpload(e.target.files[0], 'backgroundImageUrl')}
                  className="hidden"
                  disabled={uploading}
                />
              </div>
              <button
                onClick={() => onOpenImagePicker('backgroundImageUrl')}
                className="px-3 py-2 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition min-w-0 flex flex-col items-center justify-center"
                disabled={uploading}
              >
                <Image className="w-3 h-3 mb-1" />
                <span>Unsplash</span>
              </button>
            </div>
            {content.imageCredit && (
              <div className="text-xs text-gray-500">
                Photo by{' '}
                <a 
                  href={content.imageCredit.photographerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {content.imageCredit.photographer}
                </a>
                {' '}on Unsplash
              </div>
            )}
            
            {/* Style Options */}
            <div className="flex gap-1">
              <button 
                onClick={() => onContentChange('backgroundStyle', 'fill')}
                className={`px-2 py-1 text-xs border rounded ${content.backgroundStyle === 'fill' || !content.backgroundStyle ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
              >
                Fill
              </button>
              <button 
                onClick={() => onContentChange('backgroundStyle', 'fit')}
                className={`px-2 py-1 text-xs border rounded ${content.backgroundStyle === 'fit' ? 'bg-gray-200 border-gray-400' : 'border-gray-300'}`}
              >
                Fit
              </button>
            </div>

            {/* Overlay Opacity */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Overlay opacity</label>
              <div className="flex items-center gap-1">
                <span className="text-xs">○</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={content.overlayOpacity || 50}
                  onChange={(e) => onContentChange('overlayOpacity', parseInt(e.target.value))}
                  className="flex-1 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <span className="text-xs">●</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Color Theme - Always at bottom */}
      <ColorEditor
        value={content.colorScheme || 'default'}
        onChange={(value) => onContentChange('colorScheme', value)}
        label="Section Color Theme"
        showReset={true}
        resetValue="default"
        siteTheme={site?.design?.colorTheme ? {
          name: site.design.colorThemeName || 'Site Theme',
          colors: site.design.colorTheme
        } : null}
        customThemes={site?.design?.customThemes || []}
        onCreateCustomTheme={onCreateCustomTheme}
      />
    </div>
  );
};

export default ServicesEditor;